import { useQuery } from '@apollo/client';
import type { DatesSetArg, EventClickArg, EventSourceInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { AddCircle, CalendarMonth as MuiEventIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { SearchContainer } from '~/components/search';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { formatDate } from '~/lib/datetime';
import { getCalendarLocale } from '~/lib/i18n';
import { NewEventDialog } from '~/pages/event-types/detail/components/new-event-dialog';
import { CalendarEvent } from './components/calendar-event';
import { EventDialog } from './components/event-dialog';
import {
  NetworkSchedulerEventsDocument,
  type NetworkScheduler__Event as Event,
} from './queries/events.generated';

const formatUrlDate = (date: Date | string | null | undefined): string | null => {
  const formatted = formatDate(date, {
    format: { year: 'numeric', month: '2-digit', day: '2-digit' },
  });
  return formatted?.replaceAll('/', '-') ?? null;
};

const viewMap: Record<string, string> = {
  year: 'multiMonthYear',
  month: 'dayGridMonth',
  week: 'timeGridWeek',
  day: 'timeGridDay',
  list: 'listWeek',
};

const reverseMap = Object.entries(viewMap).reduce(
  (acc, [key, val]) => {
    acc[val] = key;
    return acc;
  },
  {} as Record<string, string>,
);

export const getCalendarEvents = (events: readonly Event[]): EventSourceInput => {
  return events.flatMap((event) => {
    const calendarEvents = [];

    const isDisabled = !event.canOverride.value;
    const isBuffer = !!event.contentBuffer;

    // Normal event. The url renders it as a link, which is what gives the keyboard a way to it;
    // the click handler keeps the dialog in place instead of following it.
    calendarEvents.push({
      display: 'block',
      end: event.stop,
      extendedProps: {
        event,
        isBuffer: false,
        isDisabled,
      },
      id: String(event.id),
      start: event.start,
      url: isDisabled ? undefined : `?event=${event.id}`,
    });

    // Buffer event (used when content start/stop aren't 0)
    if (isBuffer) {
      calendarEvents.push({
        display: 'background',
        end: event.contentStop,
        extendedProps: {
          event,
          isBuffer: true,
          isDisabled: false,
        },
        id: `buffer-${event.id}`,
        start: event.contentStart,
      });
    }

    return calendarEvents;
  });
};

export const Scheduler = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const calendarRoot = useRef<HTMLDivElement>(null);

  // FullCalendar gives its chevron icons role=img with no name; the buttons around them are
  // already named by their title, so the icons are decorative. The toolbar re-renders on every
  // navigation and drops the attribute again, hence the observer rather than a one-off pass.
  useEffect(() => {
    const root = calendarRoot.current;
    if (!root) return;
    const hideDecorativeIcons = () =>
      root
        .querySelectorAll('.fc-icon[role="img"]:not([aria-hidden])')
        .forEach((icon) => icon.setAttribute('aria-hidden', 'true'));
    hideDecorativeIcons();
    const observer = new MutationObserver(hideDecorativeIcons);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const view = searchParams.get('view') || 'month';
  const startDate = searchParams.get('start');
  const initialDate = startDate ? new Date(startDate) : undefined;

  // Show the dialog if we have the right query parameter
  const currentEventId = searchParams.get('event')?.trim();
  const editDialogOpen = Number(currentEventId) > 0;

  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEventStart, setNewEventStart] = useState<DateTime>();

  const { data, loading } = useQuery(NetworkSchedulerEventsDocument, {
    variables: { networkId: currentNetwork.id, search },
  });

  const eventTypes = data?.network?.allEventTypes ?? [];
  const canCreateEvents = (data?.network?.canCreateEvents.value ?? false) && eventTypes.length > 0;

  // A month cell clicks as a whole day, so keep the current time of day; time slots carry their own.
  const openNewEventAt = ({ date, allDay }: DateClickArg) => {
    const clicked = DateTime.fromJSDate(date);
    const now = DateTime.now();
    setNewEventStart(allDay ? clicked.set({ hour: now.hour, minute: now.minute }) : clicked);
    setNewEventOpen(true);
  };

  // Get the current locale for FullCalendar
  const calendarLocale = getCalendarLocale();

  const updateSearchParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        value ? updated.set(key, value) : updated.delete(key);
      });
      return updated;
    });
  };

  const openEditEventDialog = (eventId: string) => {
    updateSearchParams({ event: eventId });
  };

  const closeEditEventDialog = () => {
    updateSearchParams({ event: null });
  };

  const handleDateAndViewChange = ({ view }: DatesSetArg) => {
    updateSearchParams({
      view: reverseMap[view.type],
      start: formatUrlDate(view.currentStart),
    });
  };

  return (
    <>
      <Toolbar
        actions={
          canCreateEvents ? (
            <Button
              color="primary"
              startIcon={<AddCircle />}
              variant="contained"
              onClick={() => {
                setNewEventStart(undefined);
                setNewEventOpen(true);
              }}
            >
              {t('pages.event_types.events_tab.new_event_button')}
            </Button>
          ) : undefined
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<MuiEventIcon />}
        titleText={t('common.tabs.events')}
      />
      <LoadingPane in={loading} size={80} thickness={4}>
        <PageContainer
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: { mb: 0 },
            [theme.breakpoints.down('md')]: {
              pb: 2,
              // Force the FullCalendar toolbar onto multiple rows on mobile:
              // title alone (full width), then nav + view buttons centered
              // below it. The right chunk's flex-wrap puts the title on its
              // own line; centering aligns the buttons under it.
              '& .fc-toolbar.fc-header-toolbar .fc-toolbar-chunk': {
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.5,
              },
              '& .fc-toolbar.fc-header-toolbar .fc-toolbar-title': {
                width: '100%',
                textAlign: 'center',
                mb: 1,
              },
            },
            [theme.breakpoints.up('md')]: { pb: 2 },
          })}
        >
          <SearchContainer sx={{ position: 'absolute', display: { xs: 'none', md: 'block' } }}>
            <SearchBar placeholder={t('pages.events.search_placeholder')} search={search} />
          </SearchContainer>
          {/* The calendar takes its height from its parent, so the wrapper passes the page's on. */}
          <Box ref={calendarRoot} sx={{ height: '100%' }}>
            <FullCalendar
              allDaySlot={false}
              buttonText={{
                day: t('pages.events.calendar.buttons.day'),
                list: t('pages.events.calendar.buttons.list'),
                month: t('pages.events.calendar.buttons.month'),
                multiMonthYear: t('pages.events.calendar.buttons.year'),
                today: t('pages.events.calendar.buttons.today'),
                week: t('pages.events.calendar.buttons.week'),
              }}
              dateClick={canCreateEvents ? openNewEventAt : undefined}
              datesSet={handleDateAndViewChange}
              eventDisplay="block"
              eventClick={({ event, jsEvent }: EventClickArg) => {
                jsEvent.preventDefault();
                if (!event.extendedProps.isBuffer && !event.extendedProps.isDisabled) {
                  openEditEventDialog(event.id);
                }
              }}
              eventContent={CalendarEvent}
              eventMouseEnter={(info) => {
                const { isBuffer, isDisabled } = info.event.extendedProps;
                if (isBuffer) return;
                info.el.style.cursor = isDisabled ? 'not-allowed' : 'pointer';
              }}
              eventMouseLeave={(info) => {
                info.el.style.cursor = 'default';
              }}
              events={getCalendarEvents(data?.network?.events ?? [])}
              headerToolbar={{
                right:
                  'title prev,next today multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay,listYear',
              }}
              height="100%"
              initialDate={initialDate}
              initialView={viewMap[view]}
              locale={calendarLocale}
              nowIndicator={true}
              plugins={[
                multiMonthPlugin,
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              weekends={true}
            />
          </Box>
        </PageContainer>
      </LoadingPane>
      <NewEventDialog
        close={() => setNewEventOpen(false)}
        eventTypes={eventTypes}
        initialStart={newEventStart}
        open={newEventOpen}
      />
      {editDialogOpen && currentEventId && (
        <EventDialog close={closeEditEventDialog} open={editDialogOpen} eventId={+currentEventId} />
      )}
    </>
  );
};
